import React from 'react';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/Breadcrumb';
import { Link } from 'react-router-dom';

const InvestmentStrategies = () => {

  const investmentStrategies = [
    {
      title: "Systematic Investment Planning (SIP)",
      description: "Start with SIP in mutual funds for disciplined, long-term wealth building.",
      benefits: [
        "Rupee cost averaging reduces market timing risk",
        "Disciplined investing habit formation",
        "Power of compounding over time",
        "Start with small amounts (₹500/month)"
      ],
      tips: [
        "Choose diversified equity funds for long-term growth",
        "Increase SIP amount annually by 10-15%",
        "Stay invested for at least 5-7 years",
        "Review and rebalance portfolio annually"
      ],
      riskLevel: "Medium",
      timeHorizon: "5+ years"
    },
    {
      title: "Diversified Portfolio Approach",
      description: "Spread your investments across different asset classes to reduce risk.",
      benefits: [
        "Reduces overall portfolio risk",
        "Captures growth from different sectors",
        "Provides stability during market volatility",
        "Optimizes risk-return trade-off"
      ],
      tips: [
        "Allocate 60-70% to equity, 20-30% to debt",
        "Include international funds for global exposure",
        "Add gold (5-10%) for inflation hedge",
        "Rebalance quarterly or annually"
      ],
      riskLevel: "Medium",
      timeHorizon: "3+ years"
    },
    {
      title: "Value Investing Strategy",
      description: "Invest in undervalued stocks with strong fundamentals for long-term gains.",
      benefits: [
        "Focus on intrinsic value vs market price",
        "Long-term wealth creation potential",
        "Lower risk compared to speculative investing",
        "Builds wealth through compounding"
      ],
      tips: [
        "Research company fundamentals thoroughly",
        "Look for companies with strong balance sheets",
        "Invest in sectors you understand",
        "Hold for long-term (5+ years)"
      ],
      riskLevel: "High",
      timeHorizon: "5+ years"
    },
    {
      title: "Index Fund Investing",
      description: "Invest in low-cost index funds that track market indices.",
      benefits: [
        "Lower expense ratios compared to active funds",
        "Diversified exposure to market",
        "No fund manager risk",
        "Consistent with market performance"
      ],
      tips: [
        "Choose broad market index funds",
        "Consider Nifty 50, Sensex, or international indices",
        "Use for core portfolio allocation",
        "Combine with active funds for satellite allocation"
      ],
      riskLevel: "Medium",
      timeHorizon: "3+ years"
    },
    {
      title: "Real Estate Investment",
      description: "Invest in real estate for rental income and capital appreciation.",
      benefits: [
        "Regular rental income stream",
        "Capital appreciation over time",
        "Inflation hedge",
        "Tangible asset ownership"
      ],
      tips: [
        "Research location and growth potential",
        "Calculate rental yield before investing",
        "Consider REITs for indirect real estate exposure",
        "Factor in maintenance and property taxes"
      ],
      riskLevel: "Medium",
      timeHorizon: "5+ years"
    },
    {
      title: "Emergency Fund Strategy",
      description: "Build a liquid emergency fund before investing in other assets.",
      benefits: [
        "Financial security during emergencies",
        "Prevents forced liquidation of investments",
        "Peace of mind for risk-taking",
        "Covers 6-12 months of expenses"
      ],
      tips: [
        "Keep in high-yield savings account or FDs",
        "Separate from investment portfolio",
        "Review and adjust based on life changes",
        "Don't invest emergency fund in volatile assets"
      ],
      riskLevel: "Low",
      timeHorizon: "Immediate"
    }
  ];

  const investmentPrinciples = [
    {
      title: "Start Early",
      description: "Time is your biggest ally in wealth building. Start investing as early as possible to benefit from compounding."
    },
    {
      title: "Diversify Wisely",
      description: "Don't put all your eggs in one basket. Spread investments across different asset classes and sectors."
    },
    {
      title: "Stay Disciplined",
      description: "Consistency in investing is more important than timing the market. Stick to your investment plan."
    },
    {
      title: "Keep Costs Low",
      description: "High fees can significantly impact long-term returns. Choose low-cost investment options."
    },
    {
      title: "Focus on Long-term",
      description: "Avoid short-term market noise. Focus on long-term wealth building goals."
    },
    {
      title: "Regular Review",
      description: "Review your portfolio regularly but avoid frequent changes. Rebalance when necessary."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Investment Strategies – Master Smart Investing for Long-term Wealth"
        description="Learn proven investment strategies for long-term wealth building. Discover SIP, mutual funds, stocks, and other investment options for financial growth. Complete investment guide."
        keywords="investment strategies, wealth building, SIP, mutual funds, stock investing, portfolio diversification, financial planning, investment guide India"
        type="article"
        articleSection="Investment"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Guides', url: '/guides' },
          { name: 'Investment Strategies', url: '/guides/investment-strategies' }
        ]}
      />
      <Breadcrumb
        items={[
          { name: 'Home', url: '/' },
          { name: 'Investment Strategies', url: '/guides/investment-strategies' }
        ]}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Investment Strategies – Master Smart Investing for Long-term Wealth
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Learn proven investment strategies that successful investors use to build long-term wealth. 
            Discover how to create a diversified portfolio and make informed investment decisions.
          </p>
        </div>

        {/* Introduction */}
        <div className="card mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            Why Investment Strategy Matters
          </h2>
          <p className="text-gray-600 mb-4">
            Successful investing is not about getting rich quick, but about building sustainable wealth 
            over time. A well-thought-out investment strategy helps you navigate market volatility, 
            manage risk, and achieve your financial goals.
          </p>
          <p className="text-gray-600">
            The key is to start early, stay disciplined, and focus on long-term wealth building rather 
            than short-term market movements.
          </p>
        </div>

        {/* Investment Strategies */}
        <div className="space-y-8 mb-12">
          {investmentStrategies.map((strategy, index) => (
            <div key={index} className="card">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">📈</span>
                  </div>
                </div>
                <div className="flex-grow">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {strategy.title}
                    </h3>
                    <div className="flex space-x-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        strategy.riskLevel === 'Low' ? 'bg-green-100 text-green-800' :
                        strategy.riskLevel === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {strategy.riskLevel} Risk
                      </span>
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                        {strategy.timeHorizon}
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4">
                    {strategy.description}
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Key Benefits:</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {strategy.benefits.map((benefit, benefitIndex) => (
                          <li key={benefitIndex} className="flex items-start">
                            <span className="text-green-500 mr-2">✓</span>
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Implementation Tips:</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {strategy.tips.map((tip, tipIndex) => (
                          <li key={tipIndex} className="flex items-start">
                            <span className="text-blue-500 mr-2">•</span>
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Investment Principles */}
        <div className="card mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">
            Core Investment Principles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {investmentPrinciples.map((principle, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">{principle.title}</h3>
                <p className="text-sm text-gray-600">{principle.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* SIP Calculator CTA */}
        <div className="card bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-200">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Plan Your SIP Investment
            </h2>
            <p className="text-gray-600 mb-6">
              Use our SIP calculator to see how your investments can grow over time with systematic investing.
            </p>
            <Link
              to="/calculators/sip"
              className="btn-primary text-lg px-8 py-3 rounded-xl"
            >
              Calculate SIP Returns
            </Link>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">
            Related Articles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to="/guides/stock-market-basics"
              className="card hover:shadow-lg transition-shadow duration-200"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Stock Market Basics for Beginners
              </h3>
              <p className="text-gray-600 text-sm">
                Learn the fundamentals of stock market investing and how to get started as a beginner.
              </p>
            </Link>
            <Link
              to="/guides/tax-saving-tips"
              className="card hover:shadow-lg transition-shadow duration-200"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Tax Saving Investment Options
              </h3>
              <p className="text-gray-600 text-sm">
                Discover tax-saving investment options that help you build wealth while saving taxes.
              </p>
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
};

export default InvestmentStrategies;
